import { supabase } from '../lib/supabase';

/**
 * Normalise a period descriptor to the {period_start, period_end, period_label}
 * shape used by the availability_request_logs table. Accepts the raw smart
 * schedule shape ({ selectedPreset, dateRange:{ startDate, endDate } }) or a
 * flat { startDate, endDate, label } object.
 */
const normalisePeriod = (period) => {
  if (!period) return { period_start: null, period_end: null, period_label: null };
  const startDate = period.startDate || period.dateRange?.startDate || null;
  const endDate = period.endDate || period.dateRange?.endDate || null;
  const label = period.label || period.selectedPreset || null;
  return {
    period_start: startDate || null,
    period_end: endDate || null,
    period_label: label && ['this_week', 'next_week', 'custom'].includes(label) ? label : null,
  };
};

/**
 * Check if an availability request email was sent to a specific user in the last 7 days
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} true if email was sent recently, false otherwise
 */
const wasEmailSentRecently = async (userId) => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { data, error } = await supabase
      .from('availability_request_logs')
      .select('id')
      .eq('user_id', userId)
      .gte('sent_at', sevenDaysAgo.toISOString())
      .limit(1);

    if (error) {
      console.error('Error checking recent availability requests:', error);
      return false; // Assume not sent recently if we can't check
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('Exception checking recent availability requests:', error);
    return false;
  }
};

/**
 * Log that an availability request email was sent to a user
 * @param {string} userId - The recipient user ID
 * @param {string} groupId - The group ID
 * @param {string} requesterId - The requester user ID
 * @returns {Promise<{success: boolean, error?: any}>}
 */
const logAvailabilityRequest = async (userId, groupId, requesterId, period = null) => {
  const { period_start, period_end, period_label } = normalisePeriod(period);
  try {
    const { data, error } = await supabase
      .from('availability_request_logs')
      .insert([{
        user_id: userId,
        group_id: groupId,
        requester_id: requesterId,
        sent_at: new Date().toISOString(),
        period_start,
        period_end,
        period_label,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error logging availability request:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception logging availability request:', error);
    return { success: false, error };
  }
};

/**
 * Get group members who don't have availability data set
 * @param {string} groupId - The group ID
 * @returns {Promise<Array>} Array of members without availability
 */
const getMembersWithoutAvailability = async (groupId) => {
  try {
    const { data, error } = await supabase.rpc('get_members_without_availability_v2', {
      group_id_param: groupId
    });

    if (error) {
      console.error('Error getting members without availability:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception getting members without availability:', error);
    return [];
  }
};

/**
 * Get all group members with their availability status
 * @param {string} groupId - The group ID
 * @returns {Promise<Array>} Array of all group members with availability status
 */
const getGroupMembersAvailabilityStatus = async (groupId) => {
  try {
    const { data, error } = await supabase.rpc('get_group_members_availability_status_v2', {
      group_id_param: groupId
    });

    if (error) {
      console.error('Error getting group members availability status:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Exception getting group members availability status:', error);
    return [];
  }
};

/**
 * Request availability from group members who haven't set it
 * This function enforces per-user rate limiting (7 days) and sends emails to members without availability
 *
 * @param {string} groupId - The group ID
 * @param {string} requesterId - The user ID who is requesting availability
 * @param {string} requesterName - The name of the requester
 * @param {string} groupName - The name of the group
 * @param {string[] | null} specificMemberIds - Optional subset of members to target
 * @param {object} period - { selectedPreset, dateRange:{startDate,endDate} } or { startDate, endDate, label }
 * @returns {Promise<{success: boolean, emailsSent: number, errors: Array, skipped: Array}>}
 */
export const requestGroupAvailability = async (
  groupId,
  requesterId,
  requesterName,
  groupName,
  specificMemberIds = null,
  period = null,
) => {
  const { period_start, period_end, period_label } = normalisePeriod(period);
  try {

    // Get members without availability (or use specific member IDs if provided)
    let membersWithoutAvailability;
    if (specificMemberIds && specificMemberIds.length > 0) {
      // Look up specific members with emails using the RPC
      const { data: members } = await supabase
        .rpc('get_member_emails_for_round', {
          member_ids: specificMemberIds,
          check_group_id: groupId
        });
      membersWithoutAvailability = (members || []).map(m => ({
        user_id: m.user_id,
        display_name: m.display_name,
        email: m.email
      }));
    } else {
      membersWithoutAvailability = await getMembersWithoutAvailability(groupId);
    }

    if (membersWithoutAvailability.length === 0) {
      console.log('✅ All group members have availability set');
      return {
        success: true,
        emailsSent: 0,
        errors: [],
        skipped: [],
        message: 'All group members have availability set'
      };
    }


    const results = {
      success: true,
      emailsSent: 0,
      errors: [],
      skipped: []
    };

    // Process each member without availability. Inserting into availability_request_logs
    // fires the on_availability_request_logged trigger which calls send-availability-request
    // server-side (in-app + email + push fan-out unified).
    for (const member of membersWithoutAvailability) {
      try {
        if (member.user_id === requesterId) {
          console.log(`⏭️ Skipping ${member.display_name} - same as requester`);
          results.skipped.push({
            userId: member.user_id,
            name: member.display_name,
            reason: 'Same as requester'
          });
          continue;
        }

        // 7-day rate limit check (client-side; defence in depth could be added server-side)
        const wasRecentlySent = await wasEmailSentRecently(member.user_id);
        if (wasRecentlySent) {
          console.log(`⏭️ Skipping ${member.display_name} - email sent within last 7 days`);
          results.skipped.push({
            userId: member.user_id,
            name: member.display_name,
            reason: 'Email sent within last 7 days'
          });
          continue;
        }

        const logResult = await logAvailabilityRequest(member.user_id, groupId, requesterId, period);

        if (logResult.success) {
          console.log(`✅ Availability request queued for ${member.display_name}`);
          results.emailsSent++;
        } else {
          console.error(`❌ Failed to insert availability request log for ${member.display_name}:`, logResult.error);
          results.errors.push({
            userId: member.user_id,
            name: member.display_name,
            error: 'Failed to insert availability request log'
          });
        }

      } catch (memberError) {
        console.error(`❌ Error processing member ${member.display_name}:`, memberError);
        results.errors.push({
          userId: member.user_id,
          name: member.display_name,
          error: memberError.message
        });
      }
    }

    
    return results;

  } catch (error) {
    console.error('❌ Error in requestGroupAvailability:', error);
    return {
      success: false,
      emailsSent: 0,
      errors: [{ error: error.message }],
      skipped: []
    };
  }
};

/**
 * Request availability from friends via the SECURITY DEFINER RPC. The RPC
 * does the friendship check + per-recipient 7-day rate-limit + insert in one
 * trip; the DB trigger fans out the notification.
 *
 * @param {string[]} friendIds - User IDs to request from
 * @param {object} period - { selectedPreset, dateRange:{ startDate, endDate } } or { startDate, endDate, label }
 * @returns {Promise<{success: boolean, emailsSent: number, errors: Array, skipped: Array, results?: Array}>}
 */
export const requestFriendAvailability = async (friendIds, period = null) => {
  if (!Array.isArray(friendIds) || friendIds.length === 0) {
    return { success: true, emailsSent: 0, errors: [], skipped: [], results: [] };
  }

  const { period_start, period_end, period_label } = normalisePeriod(period);

  try {
    const { data, error } = await supabase.rpc('request_friend_availability', {
      p_friend_ids: friendIds,
      p_period_start: period_start,
      p_period_end: period_end,
      p_period_label: period_label,
    });

    if (error) {
      console.error('Error calling request_friend_availability:', error);
      return {
        success: false,
        emailsSent: 0,
        errors: [{ error: error.message }],
        skipped: [],
      };
    }

    const results = data || [];
    const emailsSent = results.filter(r => r.outcome === 'sent').length;
    const skipped = results
      .filter(r => r.outcome !== 'sent')
      .map(r => ({ userId: r.friend_id, reason: r.outcome }));

    return { success: true, emailsSent, errors: [], skipped, results };
  } catch (error) {
    console.error('Exception in requestFriendAvailability:', error);
    return {
      success: false,
      emailsSent: 0,
      errors: [{ error: error.message }],
      skipped: [],
    };
  }
};